// @ts-check
import React from "react";
import { fromJS } from "immutable";
import { SelectOption } from "@showitinc/showit-wc";
import Input from "../../../base/Input";
import GradientProperties from "../../../../components/GradientProperties";
import When from "../../../../components/utils/When";
import LeftOffset from "../../../../components/base/LeftOffset";
import { gatherProps } from "../../../../utils/Merge/MergeUtils.mjs";
import { elementSyncUtils } from "../../../../utils/SyncUtils";
import AppConstants from "../../../../constants/AppConstants";
import {
  BORDER_WIDTH,
  BORDER_COLOR,
  BORDER_STYLE,
  RECTANGLE_FILL_TYPE,
  RECTANGLE_BACKGROUND_COLOR,
  BLUR,
  BACKDROP_FILTER,
} from "../../../../constants/MergeConstants";
import SplitMergeWrapper from "../../../base/SplitMergeWrapper";
import { INPUT_TYPE_NUMBER } from "../../../../constants/ElementConstants";
import { initFormatter } from "../../../../utils/PropertyFormatters";
import {
  FILL_TYPE_COLOR,
  FILL_TYPE_GRADIENT,
  FILL_SELECTION_ADVANCED,
} from "../../../../constants/SyncConstants";
import {
  COLOR_PICKER,
  INPUT,
  SELECT,
} from "../../../../constants/InputConstants.mjs";
import { MIXED } from "../../../../constants/DesignConstants.mjs";
import { prepareInputUtils } from "../../../../utils/InputUtils";
import { formatPropUpdate } from "../../../../utils/PropertyUtils";
import CustomizeEffects from "../../shared/CustomizeEffects";
import { hasBackdropFilter, getAdvanceFilterValueFromArray } from "@/utils/Element/FilterUtils";
import AdvancedFillProperties from "./AdvancedFillProperties";
import {
  normalizeToggleFillTypeSyncUpdates,
  getFillTypeChangeUpdates,
  getPresetSelectUpdates,
  getBackdropFilterValueUpdates,
} from "@/utils/Element/SimplePropertiesFillUpdates";
import AdvancedFillPresets from "./AdvancedFillPresets";
import styles from "./SimpleProperties.module.css";

/** @typedef {import("@/constants/AdvancedFillModePresetConstants").FilterFunction} FilterFunction */

const { BG_TYPE_COLOR, DEFAULT_BORDER_PROPERTIES } = AppConstants;

const defaults = fromJS({
  fillType: BG_TYPE_COLOR,
});

const gatherableProps = [
  BORDER_STYLE,
  BORDER_WIDTH,
  BORDER_COLOR,
  RECTANGLE_FILL_TYPE,
  BLUR,
  BACKDROP_FILTER,
];

const ElementPropertiesSimple = ({
  element,
  layout,
  isLocked,
  onPropUpdate,
  preparedElement,
  mappedElement,
}) => {
  const gatheredProps = gatherProps(preparedElement, layout, gatherableProps, true);
  const borderWidth = gatheredProps.get(BORDER_WIDTH);
  const borderColor = gatheredProps.get(BORDER_COLOR);

  const elemProps = defaults.merge(gatheredProps);
  const { isSynced, toggleSyncedProp, getPropName, toggleSyncedPropMultiple } = elementSyncUtils(
    element,
    onPropUpdate,
    layout,
  );

  const separatePath = (path) => path.split(".");
  const getValue = (path) => elemProps.getIn(separatePath(path));
  const hasProp = (path) => elemProps.hasIn(separatePath(path));
  const prepChange = (path) => [path, getValue(path)];

  /**
   * Toggles the sync state for the fill type and backdrop filter.
   * @param {string} propName - The name of the prop to toggle.
   * @returns {Object} - An object with the mixed, sync, and canToggle properties.
   */
  const toggleFillType = (propName) => ({
    mixed: isSyncMixed(propName),
    sync: isSynced(propName),
    canToggle: true,
    toggleSync: () => {
      const syncUpdates = toggleSyncedPropMultiple([RECTANGLE_FILL_TYPE, BACKDROP_FILTER]);
      onPropUpdate(normalizeToggleFillTypeSyncUpdates(syncUpdates));
    },
  });

  const { onChange } = initFormatter(onPropUpdate, getPropName);
  const { onChange: onLibChange } = initFormatter(onPropUpdate);
  const { onChange: onGradientChange } = initFormatter(
    onPropUpdate,
    getPropName,
    RECTANGLE_FILL_TYPE
  );

  const _onBorderStyleChange = (propInfo) => {
    const newStyle = propInfo.value;
    if (isSyncMixed(BORDER_STYLE, preparedElement)) return;

    const updates = [propInfo];

    const isFirstBorderSetup = borderWidth === undefined && borderColor === undefined;
    if (newStyle !== "none" && isFirstBorderSetup) {
      updates.push(
        formatPropUpdate(getPropName(BORDER_WIDTH), DEFAULT_BORDER_PROPERTIES.width, borderWidth, undefined, false),
        formatPropUpdate(getPropName(BORDER_COLOR), DEFAULT_BORDER_PROPERTIES.color, borderColor, undefined, false)
      );
    }

    onPropUpdate(updates);
  };

  const { onChange: onBorderStyleChange } = initFormatter(_onBorderStyleChange, getPropName);

  const fillType = elemProps.get(RECTANGLE_FILL_TYPE);
  const isAdvancedFill = hasBackdropFilter(getValue(BACKDROP_FILTER));
  const fillProps = [
    getPropName(RECTANGLE_BACKGROUND_COLOR, RECTANGLE_FILL_TYPE),
    getValue(RECTANGLE_BACKGROUND_COLOR),
  ];

  /**
   * Handles fill type change and applies transition updates for dependent properties.
   *
   * @param {{ name: string, value: any }} propInfo - Property update object with value at `propInfo.value`
   * @returns {void}
   */
  const handleFillTypeChange = (propInfo) => {
    onPropUpdate(getFillTypeChangeUpdates(propInfo, {
      getValue,
      hasProp,
      getPropName,
    }));
  };

  const { onChange: onFillTypeChange } = initFormatter(handleFillTypeChange, getPropName);

  /**
   * Handles the selection of an advanced fill mode preset and applies it to the element's backdrop filter.
   * 
   * @param {string} presetName - The name of the preset to apply. Valid values include:
   *   - "darker" - Applies a darker blur effect
   *   - "lighter" - Applies a lighter blur effect
   *   - "glassy" - Applies a glassy blur effect
   *   - "color-tint" - Applies a color-tinted blur effect
   * @returns {void} Returns early if the preset name is not found in ADVANCED_FILL_MODE_PRESETS
   */
  const handlePresetSelect = (presetName) => {
    const updates = getPresetSelectUpdates(presetName, { getValue, getPropName });
    if (updates.length) onPropUpdate(updates);
  };

  /**
   * Creates a handler for updating a specific filter function value in the backdrop filter array.
   * 
   * @param {{ name: string, value?: * }} propInfo - Property update object with value at `propInfo.value`
   * @returns {Function} Handler function that accepts propInfo
   */
  const handleBackdropFilterValueUpdate = (propInfo) => {
    const updates = getBackdropFilterValueUpdates(propInfo, { getValue, getPropName });
    if (updates.length) onPropUpdate(updates);
  };

  const { onChange: onBackdropFilterValueChange } = initFormatter(handleBackdropFilterValueUpdate);

  /**
   * Retrieves the value of a specific filter function from the backdrop filter array.
   *
   * @typedef {BLUR | BRIGHTNESS | CONTRAST | SATURATE | INVERT}
   * @returns {number|undefined} The value of the filter function, or default if not found
   */
  const retrieveFilterValues = (filterFunctionName) => {
    const backdropFilter = getValue(BACKDROP_FILTER);
    return getAdvanceFilterValueFromArray(backdropFilter, filterFunctionName);
  };

  const { canMerge, prepareValue, isSyncMixed } = prepareInputUtils(
    layout,
    preparedElement,
    elemProps
  );

  const borderStyle = getValue(BORDER_STYLE) ?? "none";
  const showBorderChildren =
    borderStyle !== "none" &&
    borderStyle !== MIXED &&
    !isSyncMixed(BORDER_STYLE);

  return (
    <>
      <SplitMergeWrapper
        {...toggleFillType(isAdvancedFill ? BACKDROP_FILTER : RECTANGLE_FILL_TYPE)}
        layout={layout}
      >
        <Input
          data-testid="fill-select"
          context={canMerge(RECTANGLE_FILL_TYPE)}
          disabled={isLocked}
          inputType={SELECT}
          label="Fill"
          onSISelect={onFillTypeChange(...prepChange(RECTANGLE_FILL_TYPE))}
          reserveContext
          shift="left"
          value={
            isAdvancedFill 
            ? FILL_SELECTION_ADVANCED 
            : prepareValue(RECTANGLE_FILL_TYPE)
          }
        >
          <SelectOption data-testid="fill-color-option" value={FILL_TYPE_COLOR}>
            Color
          </SelectOption>
          <SelectOption
            data-testid="fill-gradient-option"
            value={FILL_TYPE_GRADIENT}
          >
            Gradient
          </SelectOption>
          <SelectOption
            data-testid="fill-advanced-option"
            value={FILL_SELECTION_ADVANCED}
          >
            Advanced
          </SelectOption>
        </Input>
        {fillType !== MIXED && !isSyncMixed(RECTANGLE_FILL_TYPE) && (
          <LeftOffset>
            <When it={fillType !== FILL_TYPE_GRADIENT && !isAdvancedFill}>
              <Input
                data-testid="shape-style-color-picker"
                color={prepareValue(RECTANGLE_BACKGROUND_COLOR)}
                disabled={isLocked}
                dataTestId="fill-color-picker"
                inputType={COLOR_PICKER}
                label="Color"
                onSIChange={onLibChange(...fillProps)}
                onSIBeforeChange={onLibChange(...fillProps)}
                onSIInput={onLibChange(...fillProps, false)}
              />
            </When>
            <When it={fillType === FILL_TYPE_GRADIENT}>
              <GradientProperties
                element={elemProps}
                disabled={isLocked}
                onChange={onGradientChange}
              />
            </When>
            <When it={isAdvancedFill}>
              <p className={styles.advancedFillTip}>
                Advanced Fills may have unexpected results when animations and transitions are applied to this element.
              </p>
            </When>
            <AdvancedFillPresets
              isLocked={isLocked}
              getValue={getValue}
              handlePresetSelect={handlePresetSelect}
              isSplit={!isSynced(RECTANGLE_FILL_TYPE)}
            />
            <When it={isAdvancedFill}>
              <Input
                color={prepareValue(RECTANGLE_BACKGROUND_COLOR)}
                disabled={isLocked}
                dataTestId="fill-color-picker"
                inputType={COLOR_PICKER}
                label="Color"
                onSIChange={onLibChange(...fillProps)}
                onSIBeforeChange={onLibChange(...fillProps)}
                onSIInput={onLibChange(...fillProps, false)}
              />
            </When>
            <AdvancedFillProperties
              isLocked={isLocked}
              getValue={getValue}
              onBackdropFilterValueChange={onBackdropFilterValueChange}
              retrieveFilterValues={retrieveFilterValues}
            />
          </LeftOffset>
        )}
      </SplitMergeWrapper>
      <SplitMergeWrapper
        layout={layout}
        mixed={isSyncMixed(BORDER_STYLE)}
        sync={isSynced(BORDER_STYLE)}
        toggleSync={() => toggleSyncedProp(BORDER_STYLE)}
      >
        <Input
          data-testid="border-style-select"
          context={canMerge(BORDER_STYLE)}
          disabled={isLocked}
          inputType={SELECT}
          label="Border"
          onSISelect={onBorderStyleChange(...prepChange(BORDER_STYLE))}
          reserveContext
          shift="left"
          value={borderStyle}
        >
          <SelectOption value="none">None</SelectOption>
          <SelectOption value="solid">Solid</SelectOption>
          <SelectOption value="dashed">Dashed</SelectOption>
          <SelectOption value="dotted">Dotted</SelectOption>
        </Input>
        {showBorderChildren && (
          <LeftOffset>
            <Input
              data-testid="border-size-input"
              disabled={isLocked}
              inputType={INPUT}
              label="Size"
              min="0"
              onSIBeforeChange={onChange(...prepChange(BORDER_WIDTH))}
              onSIInput={onChange(...prepChange(BORDER_WIDTH), false)}
              type={INPUT_TYPE_NUMBER}
              units="px"
              value={prepareValue(BORDER_WIDTH, DEFAULT_BORDER_PROPERTIES.width)}
            />
            <Input
              data-testid="shape-style-border-color-picker"
              color={prepareValue(BORDER_COLOR, DEFAULT_BORDER_PROPERTIES.color)}
              disabled={isLocked}
              inputType={COLOR_PICKER}
              label="Color"
              onSIBeforeChange={onChange(...prepChange(BORDER_COLOR))}
              onSIChange={onChange(...prepChange(BORDER_COLOR))}
              onSIInput={onChange(...prepChange(BORDER_COLOR), false)}
            />
          </LeftOffset>
        )}
      </SplitMergeWrapper>
      <CustomizeEffects
        mappedElement={mappedElement}
        layout={layout}
        onPropUpdate={onPropUpdate}
        preparedElement={preparedElement}
      />
    </>
  );
};

export default ElementPropertiesSimple;
